// /admin/crm/configuracion — horario de atención + auto-reply fuera de horario
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const DIAS = [
  { key: 'lun', label: 'Lunes' },
  { key: 'mar', label: 'Martes' },
  { key: 'mie', label: 'Miércoles' },
  { key: 'jue', label: 'Jueves' },
  { key: 'vie', label: 'Viernes' },
  { key: 'sab', label: 'Sábado' },
  { key: 'dom', label: 'Domingo' },
]

const HORARIO_DEFAULT = {
  activo: true,
  zona_horaria: 'America/Montevideo',
  dias: {
    lun: { abre: '09:00', cierra: '18:00' },
    mar: { abre: '09:00', cierra: '18:00' },
    mie: { abre: '09:00', cierra: '18:00' },
    jue: { abre: '09:00', cierra: '18:00' },
    vie: { abre: '09:00', cierra: '18:00' },
    sab: { abre: '09:00', cierra: '13:00' },
    dom: null,
  },
}

// app_config.value es TEXT. Si el value vino como string JSON, lo parseamos.
function parseConfigValue(raw) {
  if (raw == null) return null
  if (typeof raw !== 'string') return raw // ya es objeto/número/bool
  try { return JSON.parse(raw) }
  catch { return raw }
}

// Normaliza el horario asegurando que tenga estructura completa (no crashea si la DB trae algo raro)
function normalizarHorario(h) {
  if (!h || typeof h !== 'object') return HORARIO_DEFAULT
  return {
    activo: h.activo !== false,
    zona_horaria: h.zona_horaria || 'America/Montevideo',
    dias: { ...HORARIO_DEFAULT.dias, ...(h.dias || {}) },
  }
}

export default function CRMConfiguracion() {
  const [horario, setHorario] = useState(HORARIO_DEFAULT)
  const [mensaje, setMensaje] = useState('')
  const [cooldown, setCooldown] = useState(4)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [dentroAhora, setDentroAhora] = useState(null)
  const [statsLog, setStatsLog] = useState(null)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const keys = ['crm_horario_atencion', 'crm_auto_reply_fuera_horario', 'crm_auto_reply_cooldown_horas']
    const { data } = await supabase.from('app_config').select('key, value').in('key', keys)
    // app_config.value es text → parsear cada uno como JSON
    const map = Object.fromEntries((data || []).map(r => [r.key, parseConfigValue(r.value)]))
    setHorario(normalizarHorario(map.crm_horario_atencion))
    const v = map.crm_auto_reply_fuera_horario
    setMensaje(typeof v === 'string' ? v : '')
    if (map.crm_auto_reply_cooldown_horas != null) setCooldown(Number(map.crm_auto_reply_cooldown_horas) || 4)

    // Estado actual: dentro de horario?
    const { data: dentro } = await supabase.rpc('crm_esta_dentro_horario')
    setDentroAhora(dentro)

    // Stats: cuántos auto-replies enviados últimos 7 días
    const desde = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    const { count } = await supabase
      .from('crm_auto_reply_log')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', desde)
    setStatsLog({ enviados_7d: count ?? 0 })

    setLoading(false)
  }

  async function guardar() {
    setSaving(true); setMsg(null)
    try {
      // app_config.value es text → serializar todo como JSON.stringify
      const updates = [
        { key: 'crm_horario_atencion', value: JSON.stringify(horario) },
        { key: 'crm_auto_reply_fuera_horario', value: JSON.stringify(mensaje) },
        { key: 'crm_auto_reply_cooldown_horas', value: JSON.stringify(cooldown) },
      ]
      for (const u of updates) {
        const { error } = await supabase
          .from('app_config')
          .upsert({ key: u.key, value: u.value }, { onConflict: 'key' })
        if (error) throw error
      }
      setMsg({ ok: true, txt: 'Guardado.' })
      // Refrescar estado
      const { data: dentro } = await supabase.rpc('crm_esta_dentro_horario')
      setDentroAhora(dentro)
    } catch (e) {
      setMsg({ ok: false, txt: 'Error: ' + e.message })
    } finally {
      setSaving(false)
      setTimeout(() => setMsg(null), 4000)
    }
  }

  function toggleDia(d) {
    setHorario(h => ({
      ...h,
      dias: {
        ...h.dias,
        [d]: h.dias[d] === null ? { abre: '09:00', cierra: '18:00' } : null,
      },
    }))
  }
  function setDiaCampo(d, campo, val) {
    setHorario(h => ({
      ...h,
      dias: {
        ...h.dias,
        [d]: { ...(h.dias[d] || {}), [campo]: val },
      },
    }))
  }

  return (
    <AdminLayout>
      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1>Configuración CRM</h1>
            <p className="lead">Horario de atención y respuestas automáticas.</p>
          </div>
          <Link to="/admin/crm" className="btn ghost">← Volver al CRM</Link>
        </div>

        {/* Estado actual */}
        {dentroAhora !== null && (
          <div className="card" style={{
            background: dentroAhora ? '#D1FAE5' : '#FEF3C7',
            borderLeft: '4px solid ' + (dentroAhora ? '#16A34A' : '#D97706'),
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 22 }}>{dentroAhora ? '🟢' : '🌙'}</span>
            <div>
              <strong>{dentroAhora ? 'Dentro de horario' : 'Fuera de horario'}</strong>
              <div style={{ fontSize: 13, color: '#475569' }}>
                {dentroAhora
                  ? 'Los mensajes entrantes NO disparan auto-reply.'
                  : 'Los mensajes entrantes disparan auto-reply (con cooldown de ' + cooldown + ' h).'}
              </div>
            </div>
            {statsLog && (
              <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 12 }}>
                <strong style={{ fontSize: 18 }}>{statsLog.enviados_7d}</strong>
                <div style={{ color: '#64748b' }}>auto-replies últimos 7d</div>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="card">Cargando…</div>
        ) : (
          <>
            {/* Toggle global */}
            <div className="card">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={horario.activo}
                  onChange={e => setHorario(h => ({ ...h, activo: e.target.checked }))}
                  style={{ width: 'auto' }}
                />
                <strong>Auto-reply fuera de horario activo</strong>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Si lo apagás, no se envía nada automáticamente aunque el cliente escriba a las 3 AM.
                </span>
              </label>
            </div>

            {/* Horario por día */}
            <h2 className="section-head">Horario por día</h2>
            <div className="card">
              <table style={{ width: '100%' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                    <th style={th}>Día</th>
                    <th style={th}>Atendemos</th>
                    <th style={th}>Abre</th>
                    <th style={th}>Cierra</th>
                  </tr>
                </thead>
                <tbody>
                  {DIAS.map(d => {
                    const dia = horario.dias[d.key]
                    const activo = dia !== null
                    return (
                      <tr key={d.key} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={td}><strong>{d.label}</strong></td>
                        <td style={td}>
                          <label style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={activo}
                              onChange={() => toggleDia(d.key)}
                              style={{ width: 'auto', marginRight: 6 }}
                            />
                            {activo ? 'Sí' : 'Cerrado'}
                          </label>
                        </td>
                        <td style={td}>
                          {activo && (
                            <input
                              type="time"
                              value={dia?.abre || '09:00'}
                              onChange={e => setDiaCampo(d.key, 'abre', e.target.value)}
                              style={inputStyle}
                            />
                          )}
                        </td>
                        <td style={td}>
                          {activo && (
                            <input
                              type="time"
                              value={dia?.cierra || '18:00'}
                              onChange={e => setDiaCampo(d.key, 'cierra', e.target.value)}
                              style={inputStyle}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mensaje y cooldown */}
            <h2 className="section-head">Mensaje automático</h2>
            <div className="card">
              <label style={{ display: 'block', marginBottom: 14 }}>
                <b style={labelStyle}>Texto que se envía cuando un cliente escribe fuera de horario</b>
                <textarea
                  value={mensaje}
                  onChange={e => setMensaje(e.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontFamily: 'inherit', fontSize: 14, resize: 'vertical' }}
                  placeholder="Ej: ¡Hola! Recibimos tu mensaje 🙌. Estamos fuera de horario pero te respondemos en cuanto volvamos…"
                />
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                  Tip: mantenelo corto y cálido. Cita el horario para que el cliente sepa cuándo volvés.
                </div>
              </label>

              <label style={{ display: 'block' }}>
                <b style={labelStyle}>Cooldown entre auto-replies (horas)</b>
                <input
                  type="number"
                  min="1"
                  max="48"
                  value={cooldown}
                  onChange={e => setCooldown(Math.max(1, Math.min(48, Number(e.target.value) || 4)))}
                  style={{ ...inputStyle, width: 80 }}
                />
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                  Tiempo mínimo antes de mandar otro auto-reply al mismo cliente. Evita que se acumulen N mensajes idénticos si escribe varias veces seguidas a las 3 AM.
                </div>
              </label>
            </div>

            {/* Acciones */}
            <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className="btn primary" onClick={guardar} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar configuración'}
              </button>
              {msg && (
                <span style={{
                  fontSize: 13, fontWeight: 600,
                  color: msg.ok ? '#15803D' : '#B91C1C',
                }}>{msg.txt}</span>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  )
}

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em' }
const td = { padding: '10px 12px', fontSize: 14 }
const inputStyle = { padding: '6px 10px', border: '1.5px solid #E2E8F0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#1A3461', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }
